import { MESSAGE_CORRECTION_STATUS_LABELS, type MessageCorrection, type OnboardingChatMessage, type ProjectPendingItem } from '@lia/contracts';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { StatusBadge } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { Checkbox } from '../primitives/checkbox.js';
import { FormField } from '../primitives/form-field.js';
import { Input, Textarea } from '../primitives/input.js';

// v1 learning + onboarding (presentational): mark a LIA reply with a team note, the correction card shown under the
// original reply (test reply, editable instruction, save as test case), the onboarding chat thread and pending items.

export function MarkReplyForm({ onSubmit, busy, className }: { onSubmit: (note: string) => void; busy?: boolean; className?: string }) {
  const [note, setNote] = React.useState('');
  return (
    <form
      className={cn('grid gap-2', className)}
      onSubmit={(e) => {
        e.preventDefault();
        if (note.trim().length >= 3) onSubmit(note.trim());
      }}
    >
      <FormField label="¿Cómo habrías querido que respondiera?" description="Nota interna del equipo: no se envía al prospecto.">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} disabled={busy} />
      </FormField>
      <div>
        <Button type="submit" size="sm" loading={busy} disabled={note.trim().length < 3}>
          Generar respuesta de prueba
        </Button>
      </div>
    </form>
  );
}

export interface CorrectionCardProps {
  correction: Pick<MessageCorrection, 'note' | 'status' | 'testReply' | 'testReplyError' | 'instruction' | 'proposedInstruction' | 'appliedDraftVersion' | 'testScenarioId' | 'createdByName'>;
  canManage?: boolean;
  busy?: boolean;
  onSaveInstruction?: (text: string) => void;
  onApply?: () => void;
  onReject?: () => void;
  onSaveCase?: (v: { name: string; criterion: string; mustPass: boolean }) => void;
  className?: string;
}

export function CorrectionCard({ correction: c, canManage, busy, onSaveInstruction, onApply, onReject, onSaveCase, className }: CorrectionCardProps) {
  const [text, setText] = React.useState(c.instruction ?? '');
  const [caseOpen, setCaseOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [criterion, setCriterion] = React.useState(c.note);
  const [mustPass, setMustPass] = React.useState(false);
  React.useEffect(() => setText(c.instruction ?? ''), [c.instruction]);
  const open = c.status === 'proposed';
  return (
    <section aria-label="Corrección del equipo" className={cn('grid gap-2 rounded-card border border-border-subtle bg-surface-subtle p-3', className)}>
      <p className="flex flex-wrap items-center gap-2 text-meta text-text-secondary">
        <StatusBadge tone={c.status === 'applied' ? 'success' : c.status === 'rejected' ? 'neutral' : 'info'}>{MESSAGE_CORRECTION_STATUS_LABELS[c.status]}</StatusBadge>
        <span>Nota del equipo{c.createdByName ? ` (${c.createdByName})` : ''}: {c.note}</span>
      </p>
      <div className="grid gap-1">
        <p className="text-meta font-medium text-text-secondary">Respuesta de prueba · solo equipo, no se envió</p>
        {c.testReply ? <p className="whitespace-pre-wrap text-body text-text-primary">{c.testReply}</p> : <p className="text-body text-text-secondary">{c.testReplyError ?? 'Sin respuesta de prueba'}</p>}
      </div>
      {canManage && open ? (
        <FormField label="Instrucción propuesta para el proyecto" description="Edítala con tus palabras; se agrega al borrador y se activa al publicarlo (con las pruebas obligatorias aprobadas).">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000} disabled={busy} />
        </FormField>
      ) : c.instruction ? (
        <p className="text-body text-text-primary">
          <span className="font-medium">Instrucción: </span>
          {c.instruction}
          {c.appliedDraftVersion ? ` · borrador v${c.appliedDraftVersion}` : ''}
        </p>
      ) : null}
      {canManage ? (
        <div className="flex flex-wrap gap-2">
          {open ? (
            <>
              <Button size="sm" variant="secondary" disabled={busy || text.trim().length < 3 || text.trim() === (c.instruction ?? '')} onClick={() => onSaveInstruction?.(text.trim())}>
                Guardar redacción
              </Button>
              <Button size="sm" disabled={busy || !(c.instruction ?? '').trim()} onClick={onApply}>
                Agregar al borrador del proyecto
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={onReject}>
                Descartar
              </Button>
            </>
          ) : null}
          {!c.testScenarioId && c.status !== 'rejected' ? (
            <Button size="sm" variant="ghost" onClick={() => setCaseOpen((v) => !v)}>
              Guardar este caso para futuras pruebas
            </Button>
          ) : null}
          {c.testScenarioId ? <StatusBadge tone="success">Caso guardado como prueba</StatusBadge> : null}
        </div>
      ) : null}
      {caseOpen && canManage && !c.testScenarioId ? (
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim().length >= 2 && criterion.trim().length >= 3) onSaveCase?.({ name: name.trim(), criterion: criterion.trim(), mustPass });
          }}
        >
          <FormField label="Nombre del caso">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </FormField>
          <FormField label="Comportamiento esperado" description="Se evalúa en cada ejecución: Cumple / No cumple.">
            <Textarea value={criterion} onChange={(e) => setCriterion(e.target.value)} rows={2} maxLength={500} />
          </FormField>
          <Checkbox label="Debe pasar antes de activar cambios" description="No se puede publicar una nueva versión del proyecto si este caso no pasa con ella." checked={mustPass} onCheckedChange={(v) => setMustPass(v === true)} />
          <div>
            <Button type="submit" size="sm" disabled={busy}>
              Guardar caso
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

export function ChatThread({ messages, className }: { messages: OnboardingChatMessage[]; className?: string }) {
  return (
    <ol className={cn('grid gap-3', className)} aria-label="Conversación">
      {messages.map((m, i) => (
        <li key={i} className={cn('grid max-w-[80ch] gap-1 rounded-card p-3', m.role === 'user' ? 'justify-self-end bg-surface-subtle' : 'border border-border-subtle')}>
          <p className="text-meta font-medium text-text-secondary">{m.role === 'user' ? 'Tú' : 'LIA'}</p>
          <p className="whitespace-pre-wrap text-body text-text-primary">{m.text}</p>
          {m.tools.length ? (
            <ul className="flex flex-wrap gap-1" aria-label="Acciones realizadas">
              {m.tools.map((t, j) => (
                <li key={j}>
                  <StatusBadge tone={t.ok ? 'success' : 'danger'}>{t.ok ? t.name : `${t.name} (falló)`}</StatusBadge>
                </li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

export function PendingItemList({ items, onClose, className }: { items: Pick<ProjectPendingItem, 'id' | 'title' | 'ownerLabel' | 'closingCondition' | 'status' | 'closedNote'>[]; onClose?: (id: string) => void; className?: string }) {
  if (!items.length) return <p className={cn('text-body text-text-secondary', className)}>Sin pendientes.</p>;
  return (
    <ul className={cn('grid divide-y divide-border-subtle', className)}>
      {items.map((p) => (
        <li key={p.id} className="flex flex-wrap items-start gap-2 py-2">
          <StatusBadge tone={p.status === 'open' ? 'warning' : 'success'}>{p.status === 'open' ? 'Pendiente' : 'Cerrado'}</StatusBadge>
          <div className="grid min-w-0 flex-1 gap-0.5">
            <p className="text-body font-medium text-text-primary">{p.title}</p>
            <p className="text-meta text-text-secondary">
              Responsable: {p.ownerLabel} · Se cierra cuando: {p.closingCondition}
            </p>
            {p.closedNote ? <p className="text-meta text-text-secondary">Cierre: {p.closedNote}</p> : null}
          </div>
          {p.status === 'open' && onClose ? (
            <Button size="sm" variant="ghost" onClick={() => onClose(p.id)}>
              Cerrar
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
